import React, { useState, useEffect, Fragment } from "react";
import {
  Clock,
  Building2,
  MessageCircle,
  Globe,
  ArrowRight,
  UserPlus,
  Users,
  X,
  Check,
} from "lucide-react";
import AgentAssignmentModal from "./AgentAssigmentModal";
import SuccessDialog from "./SuccessDialog";
import {
  Typography,
  Box,
  Button,
  Card,
  Avatar,
  Chip,
  Badge,
} from "./UIComponents";
import Phone from "./Phone";
import {
  getLeadTemperatureColor,
  getLeadStatusColor,
  formatDate,
} from "../utils/helpers";

const LeadCard = ({
  lead,
  conversationLength,
  onViewDetails,
  agentlist,
  assignedagent,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [assignedAgent, setAssignedAgent] = useState(null);

  useEffect(() => {
    if (assignedagent && agentlist?.length > 0) {
      const agent = agentlist.find(
        (agent) => agent.id === Number(assignedagent)
      );
      setAssignedAgent(agent || null);
    } else {
      setAssignedAgent(null);
    }
  }, [assignedagent, agentlist]);

  const handleModalOpen = () => setIsModalOpen(true);
  const handleModalClose = () => setIsModalOpen(false);

  const handleAssignSuccess = (agent) => {
    setSuccessDialogOpen(true);
    setAssignedAgent(agent);
  };

  const temperatureColor = getLeadTemperatureColor(lead.lead_type);
  const statusColor = getLeadStatusColor(lead.status || "new");

  return (
    <Fragment>
      <Card
        className="group transition-all duration-300 hover:shadow-lg hover:translate-y-[-2px]"
        backgroundColor="white"
        borderColor={temperatureColor.border}
      >
        <div className="p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
            <div className="sm:mr-2">
              <Avatar
                initials={lead.full_name.charAt(0)}
                backgroundColor={temperatureColor.name}
                textColor={temperatureColor.dark}
                size="lg"
                className="group-hover:scale-105 transition-transform"
              />
            </div>

            <div className="flex-1">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
                <div>
                  <Typography
                    variant="h6"
                    className="mb-1 font-semibold group-hover:text-blue-600 transition-colors"
                  >
                    {lead.full_name}
                  </Typography>

                  <div className="flex flex-wrap gap-3 text-gray-600 mb-2">
                    <div className="flex items-center gap-1.5 hover:text-blue-600 transition-colors">
                      <Phone size={16} className="stroke-current" />
                      <Typography variant="body2">{lead.phone}</Typography>
                    </div>

                    <div className="flex items-center gap-1.5 hover:text-blue-600 transition-colors">
                      <Clock size={16} className="stroke-current" />
                      <Typography variant="body2">
                        {formatDate(lead.created_time)}
                      </Typography>
                    </div>

                    {lead.company && (
                      <div className="flex items-center gap-1.5 hover:text-blue-600 transition-colors">
                        <Building2 size={16} className="stroke-current" />
                        <Typography variant="body2">{lead.company}</Typography>
                      </div>
                    )}
                  </div>
                </div>

                <Box className="flex gap-2 mt-2 sm:mt-0">
                  <Chip
                    label="New Lead"
                    color={statusColor}
                    variant="subtle"
                    size="small"
                  />
                  <Chip
                    label="WhatsApp"
                    color={statusColor}
                    variant="subtle"
                    size="small"
                  />
                </Box>
              </div>

              {assignedAgent && (
                <div className="mb-4 bg-blue-50 rounded-lg p-3 flex items-center gap-3 border border-blue-100">
                  <Avatar
                    initials={assignedAgent.full_name.charAt(0)}
                    size="sm"
                    backgroundColor="blue"
                  />
                  <div>
                    <Typography variant="caption" className="text-gray-600">
                      Assigned To
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      className="font-medium text-blue-700"
                    >
                      {assignedAgent.full_name}
                    </Typography>
                  </div>
                </div>
              )}

              <div className="h-px bg-gray-200 my-4" />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                <div className="flex items-center gap-3 bg-blue-50 px-3 py-2.5 rounded-lg">
                  <MessageCircle size={20} className="text-blue-600" />
                  <div>
                    <Typography variant="caption" className="text-gray-600">
                      Conversations
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      className="text-blue-600 font-semibold"
                    >
                      {lead?.conversation_count || 0}
                    </Typography>
                  </div>
                </div>

                <div
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg ${temperatureColor.bgLight}`}
                >
                  <Globe size={20} className={temperatureColor.text} />
                  <div>
                    <Typography variant="caption" className="text-gray-600">
                      Site Visit Intent
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      className={`${temperatureColor.text} font-semibold`}
                    >
                      {lead.lead_type}
                      <Badge color={temperatureColor.name} className="ml-2" />
                    </Typography>
                  </div>
                </div>
              </div>

              <Box className="flex gap-3 justify-end">
                <Button
                  variant="outlined"
                  startIcon={<UserPlus size={16} />}
                  onClick={handleModalOpen}
                  color="primary"
                >
                  {assignedAgent ? "Update Agent" : "Assign Agent"}
                </Button>

                <Button
                  variant="contained"
                  endIcon={<ArrowRight size={16} />}
                  onClick={() => onViewDetails(lead)}
                  color="primary"
                >
                  View Details
                </Button>
              </Box>
            </div>
          </div>
        </div>
      </Card>
      <AgentAssignmentModal
        open={isModalOpen}
        onClose={handleModalClose}
        agentList={agentlist}
        currentAgentId={assignedagent}
        leadId={lead.id}
        onAssignSuccess={handleAssignSuccess}
      />

      <SuccessDialog
        open={successDialogOpen}
        onClose={() => setSuccessDialogOpen(false)}
        type={assignedagent ? "update" : "assign"}
      />
    </Fragment>
  );
};

export default LeadCard;
